import type { FilterRuleEntity } from '@/shared/components/query-builder'
import type { ChangeEvent, FormEvent } from 'react'
import type { FieldViewProps, ValueState } from '../../types'
import type NumberController from './Controller'

import React, { Fragment, useLayoutEffect, useRef, useState } from 'react'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Operators } from '@/shared/components/query-builder'
import { isReferenceRule } from '@/shared/components/query-builder/utils/utils'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Radio } from '@/shared/components/ui/radio'

import { Group } from '../../components/InputGroup'

type NumberViewProps = FieldViewProps<NumberController>
type State = {
	single: string
	operation: string
	gt: string
	lt: string
}

const getInitialState = (storedValue: ValueState<string | object>) => {
	const { operation, value } = storedValue
	const base = { gt: '', lt: '', operation, single: '' }

	return typeof value === 'number' || typeof value === 'string'
		? { ...base, single: value }
		: { ...base, ...value }
}

export default function NumberView({
	storedValue,
	field,
	invalidMessage,
	localValue,
	isRemovable,
	onRemove,
	onApply,
	onChange,
	closePopup,
}: NumberViewProps) {
	const { t } = useTranslation()

	const focusTimeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const nextInputRef = useRef<HTMLInputElement | null>(null)
	const [state, setState] = useState<State>(() => getInitialState(storedValue))
	const focusNextInput = () => {
		const target = nextInputRef.current
		if (target) {
			focusTimeoutIdRef.current = setTimeout(() => {
				nextInputRef.current?.focus()
			}, 10)
		}
	}
	useLayoutEffect(() => {
		focusNextInput()

		return () => {
			focusTimeoutIdRef.current && clearTimeout(focusTimeoutIdRef.current)
		}
	}, [])
	const isBetween = state.operation === Operators.BETWEEN
	const filterTypes = field.getFilterTypes(t)
	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		if (invalidMessage) return
		onApply?.()
		closePopup?.()
	}
	const onChangeCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
		const operation = event.target.value as Operators
		const isNotSet = [Operators.IS_EMPTY, Operators.IS_NOT_EMPTY].includes(
			operation
		)
		const isBetween = operation === Operators.BETWEEN
		const isKeyboardEvent =
			(event.nativeEvent as PointerEvent).screenX === 0 &&
			(event.nativeEvent as PointerEvent).screenY === 0
		const newState = { ...state, operation }
		setState(newState)
		if (!isKeyboardEvent) {
			focusNextInput()
		}

		const { gt, lt } = newState
		let value: string | { gt: string; lt: string } = newState.single
		if (isBetween) {
			value = { gt, lt }
		} else if (isNotSet) {
			value = '1'
		}

		onChange({ operation, value })
	}
	const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
		const { name } = event.target
		const val = event.target.value

		const { operation } = state
		const newState = { ...state, [name]: val }
		setState(newState)
		const { gt, lt } = newState
		const value = isBetween ? { gt, lt } : val
		onChange({ operation, value })
	}
	const { operation } = state
	const isInvalid = Boolean(invalidMessage)

	return (
		<Group
			onSubmit={handleSubmit}
			isDisabledApply={Boolean(isInvalid || field.validate(localValue, t))}
			onRemove={isRemovable ? onRemove : undefined}
		>
			<div className='flex max-w-[260px] min-w-[260px] flex-col gap-3 p-3'>
				{isReferenceRule(storedValue as FilterRuleEntity) && (
					<Alert variant='warning'>
						<InfoIcon />
						<AlertDescription className='text-xs'>
							{t('general_reference_rule_description', {
								defaultValue:
									'This filed now used as referance to another field.',
							})}
						</AlertDescription>
					</Alert>
				)}
				<div className='flex flex-col gap-1'>
					{filterTypes.map((m) => {
						const isCurrent = m.operation === operation

						return (
							<Fragment key={m.operation}>
								<div className='mt-2 flex items-center gap-2'>
									<Radio
										id={`radio-mode-${m.operation}`}
										checked={isCurrent}
										name='mode'
										onChange={onChangeCheckbox}
										value={m.operation}
									/>
									<Label
										className='font-normal'
										htmlFor={`radio-mode-${m.operation}`}
									>
										{m.label}
									</Label>
								</div>

								{isCurrent && m.hasInput ? (
									<>
										{m.operation === Operators.BETWEEN ? (
											<div className='flex items-center gap-2'>
												<Input
													ref={nextInputRef}
													name='gt'
													invalid={isInvalid}
													onChange={onChangeInput}
													autoComplete='off'
													autoCorrect='off'
													type='number'
													value={state.gt}
												/>
												<Input
													name='lt'
													invalid={isInvalid}
													onChange={onChangeInput}
													type='number'
													autoComplete='off'
													autoCorrect='off'
													value={state.lt}
												/>
											</div>
										) : (
											<Input
												ref={nextInputRef}
												invalid={isInvalid}
												name='single'
												onChange={onChangeInput}
												type='number'
												autoComplete='off'
												autoCorrect='off'
												value={state.single}
											/>
										)}
										{invalidMessage && (
											<div className='text-sm text-destructive'>
												{invalidMessage}
											</div>
										)}
									</>
								) : null}
							</Fragment>
						)
					})}
				</div>
				{field.note && (
					<div className='text-sm text-muted-foreground'>{field.note}</div>
				)}
			</div>
		</Group>
	)
}
